import { Sector } from "./utils/lighthouseConstants";
import { NFSLAND_APPCONFIG } from "./constants/appConfig.ts";

export interface ArcGISConfig {
    BaseUrl: string;
    AppClientId: string;
    SksdFeatureServer: string;
    VersionManagementService: string;
}

export interface NFSAPIConfig {
    BaseApiUrl: string;
}

export interface LogApiConfig {
    BaseApiUrl: string;
}

export interface FDVConfig {
    BaseUrl: string;
    ObjectPropertiesBase: string;
    WorkOrderBase: string;
}

export interface BasemapConfig {
    Name: string;
    ServiceUrl: string;
    ThumbnailUri: string;
}

export interface RoleAccessConfig {
    VersionAdmin: string[];
}

export interface JobsConfig {
    MeasurementPointsLayerId: number;
    JobsLayerId: number;
    StepsLayerId: number;
    AttachmentsLayerId: number;
    MeasurementsDocumentationLayerId: number;
    RelatedObjectsLayerId: number;
    WorkOrderLayerId: number;
}

export interface FeatureEditorConfig {
    SecondaryFeatureCopyAttributeConfig: SecondaryFeatureCopyAttributeConfig;
    ReadonlyFields: string[];
    ExcludedFields: string[];
}

export interface AppConfigInterface {
    Environment: string;
    Version: string;
    NFSAPI: NFSAPIConfig;
    LogApi: LogApiConfig;
    FDV: FDVConfig;
    ArcGIS: ArcGISConfig;
    RoleAccess: RoleAccessConfig;
    GroupAccess: {
        Nautiker: string;
        SksdAdmin: string;
        BoatStatus: string;
    };
    NFSMapFeaturesPortalItemId: string;
    NFSMapFeatureServer: string;
    AdminFeatureServer: string;
    Basemaps: BasemapConfig[];
    InitialBasemap: string;
    HeightmapUrl: string;
    MorildExportServiceUrl: string;
    MorildLayerUrl: string;
    MerkeplanAreaLayerUrl: string;
    RasterProcessingGpServiceUrl: string;
    RasterUploadGpServiceUrl: string;
    KyvDepthRasterLayerUrl: string;
    KyvDepthFeatureServerUrl: string;
    NauticalObjectsFeatureServer: string;
    OperativeObjectsFeatureServer: string;
    OperativeObjectsPortalItemId: string;
    MainObjectLayerNames: string[];
    MainObjectLayerIds: number[];
    EFSLayerNames: string[];
    EFSTemplateLayerUrl: string;
    Layers: LayersConfig[];
    Scale: ScalesConfig;
    Projection: ProjectionConfig;
    CreateObject: CreateObjectGroupConfig[];
    Lighthouse: LighthouseConfig;
    JobsConfig: JobsConfig;
    MaxCatalogItems: number;
    DiscontinuedOpacity: number;
    FeatureEditor: FeatureEditorConfig;
    BoatStatusDefaultBoats: string[];
    CadToLayerUploadUrl: string;
    CadToLayerGPServiceUrl: string;
    CadLayerGroupId: string;
    FeatureToggles: {
        DroneControlJobs: boolean;
        /**
         * Lets the fast path send SKSD-requiring changes on Lys/IB objects that are
         * Prosjektert/Eksisterende in planroot, setting them Operativ. Temporary, for
         * Kystverket's lamp replacement project.
         */
        FastPathSksdOnProjectedExisting: boolean;
    };
    droneResultsLayerPortalItemId: string;
}

export const AppConfig: AppConfigInterface = {
    ...window.NFSLAND_CONFIG,
    ...NFSLAND_APPCONFIG,
};

export interface LayersConfig {
    Name: string;
    LayerId: number;
    zoomScale: number;
    SearchFields: string[];
    TitleField: string;
    ImportantFields: string[];
    FieldOrder: string[];
    StatusField: string;
    ExcludedFields: string[];
    ReadonlyFields: string[];
    IsTable: boolean;
    Type: string;
    Order: number;
    DefaultVisible: boolean;
    HasPopup: boolean;
    IsVersioned: boolean;
    ExcludeFromSearch: boolean;
    RelationshipChainToMainFeature: string[];
    RelationshipDetails?: {
        relation1LayerId: number;
        relation2LayerId: number;
        relation1FieldName: string;
        relation2FieldName: string;
    };
}

export interface ScalesConfig {
    Scales: number[];
    DefaultScale: number;
    MinScale: number;
    MaxScale: number;
}

export interface ProjectionConfig {
    CenterMeridian: number;
}

export interface CreateObjectDefaultValuesConfig {
    FieldName: string;
    Value: any;
}

export interface CreateObjectConfig {
    Title: string;
    LayerName: string; // This should be the same as LayerConfig.Name
    FieldValues: CreateObjectDefaultValuesConfig[];
    RelatedLayers: RelatedLayerConfig[];
}

export interface CreateObjectGroupConfig {
    GroupName: string;
    GroupEntries: CreateObjectConfig[];
}

export interface RelatedLayerConfig {
    LayerName: string;
    FieldValues?: CreateObjectDefaultValuesConfig[];
    RelatedLayers?: RelatedLayerConfig[];
}

export interface LighthouseConfig {
    DefaultSectors: Sector[];
    MinSectorLineLength: number;
    MaxSectorLineLength: number;
    MinSectorThickness: number | null;
    MaxSectorThickness: number | null;
    AdaptiveSectorThickness: boolean;
    DefaultSectorThickness: number;
}

export interface SecondaryFeatureCopyAttributeConfig {
    AllSecondary: string[];
    IBSecondary: string[];
    /** Only copied where primary and secondary share the value (HIB → IB, Lysbøye → Flytende merke). */
    HibLysboyeSecondary: string[];
}
